import { useTranslation } from 'react-i18next'
import { useParams, useLocation } from 'react-router-dom'
import { MainContentArea } from '@/components/layout/MainContentArea'
import { PracticeAreaContent } from '@/components/content/PracticeAreaContent'
import { PracticeContent } from '@/components/content/PracticeContent'
import { FrameworkContent } from '@/components/content/FrameworkContent'
import { useContentBundle } from '@/hooks/useContentBundle'
import { useAppStore } from '@/store/appStore'

export function ContentPage() {
  const { t } = useTranslation()
  const { practiceAreaId, practiceId, frameworkId } = useParams()
  const { hash } = useLocation()

  const { data: bundle, isLoading } = useContentBundle()
  const { selectedLanguage } = useAppStore()

  const highlightTerm = hash.startsWith('#highlight=')
    ? decodeURIComponent(hash.slice('#highlight='.length))
    : undefined

  // Parse #cc-{id} and optional &highlight={query} from context block navigation
  const ccMatch = hash.match(/^#cc-(\d+)/)
  const scrollToCcId = ccMatch ? Number(ccMatch[1]) : undefined
  const ccHighlightTerm = scrollToCcId
    ? (() => {
        const hlMatch = hash.match(/[&?]highlight=([^&]*)/)
        return hlMatch ? decodeURIComponent(hlMatch[1]) : undefined
      })()
    : undefined

  if (isLoading) {
    return (
      <MainContentArea>
        <div className="loading-state" aria-busy="true">
          {t('content.loadingInLanguage', { language: selectedLanguage.toUpperCase() })}
        </div>
      </MainContentArea>
    )
  }

  if (!bundle) {
    return (
      <MainContentArea>
        <div className="error-state">{t('content.unavailable')}</div>
      </MainContentArea>
    )
  }

  if (practiceAreaId) {
    const pa = bundle.practiceAreas.find((p) => p.id === Number(practiceAreaId))
    if (!pa) {
      return (
        <MainContentArea>
          <div className="not-found">{t('pages.practiceAreaNotFound')}</div>
        </MainContentArea>
      )
    }
    return <PracticeAreaContent key={pa.id} practiceArea={pa} bundle={bundle} highlightTerm={highlightTerm ?? ccHighlightTerm} scrollToCcId={scrollToCcId} />
  }

  if (practiceId) {
    const practice = bundle.practices.find((p) => p.id === Number(practiceId))
    if (!practice) {
      return (
        <MainContentArea>
          <div className="not-found">{t('pages.practiceNotFound')}</div>
        </MainContentArea>
      )
    }
    // Render directly (no MainContentArea wrapper) so content-with-note-panel fills the outlet
    return <PracticeContent key={practice.id} practice={practice} bundle={bundle} highlightTerm={highlightTerm ?? ccHighlightTerm} scrollToCcId={scrollToCcId} />
  }

  if (frameworkId) {
    const framework = bundle.externalFrameworks?.find((f) => f.id === Number(frameworkId))
    if (!framework) {
      return (
        <MainContentArea>
          <div className="not-found">{t('pages.contentNotFound')}</div>
        </MainContentArea>
      )
    }
    return <FrameworkContent key={framework.id} framework={framework} bundle={bundle} />
  }

  return (
    <MainContentArea>
      <div className="not-found">{t('pages.contentNotFound')}</div>
    </MainContentArea>
  )
}
